/*
 * The bot glyph, drawn here rather than taken from `Icon` because it moves.
 *
 * Geometry is lucide's own (`bot`, ISC); the motion is lucide-animated's, which
 * drives it with `motion/react`. Reproduced in CSS because this app has no
 * animation runtime and one icon is not a reason to take on framer-motion.
 *
 * It had a `bot-message-square` twin, dropped because that glyph's body is a
 * speech bubble and the tail reads at small sizes as a stray line beside the
 * bot rather than as part of it. Squaring the tail off would have drawn `bot`,
 * which is this one — so the tail was the icon, and not wanting it meant not
 * wanting the icon. Its wobble, bubble-swell and typing dots went with it.
 *
 * It animates on hover, as the original does. `active` loops instead, for a
 * placeholder that has to look alive without being pointed at.
 */

import type { CSSProperties } from 'react';

import styles from './BotIcon.module.css';

interface BotIconProps {
  size?: number;
  /** Loop the animation rather than waiting for a hover. */
  active?: boolean;
}

function className(active: boolean | undefined, base: string | undefined): string {
  return [base, active && styles.active].filter(Boolean).join(' ');
}

/**
 * Where a part turns or closes, in view-box units.
 *
 * `transform-box: fill-box` with `center` is the obvious way to write this and
 * does not work: an eye is a vertical line, so its fill box is two units tall
 * and *zero wide*, and a degenerate reference box leaves the origin undefined.
 * The scale then either does nothing or collapses the eye toward the corner of
 * the glyph. These are constants of a fixed drawing, so naming them is both
 * exact and cheaper than asking the browser to measure a box with no width.
 */
function originAt(x: number, y: number): CSSProperties {
  return { transformOrigin: `${x}px ${y}px` };
}

/** Lucide `bot`. Blinks; that is the whole of the original's animation. */
export function BotIcon({ size = 15, active }: BotIconProps) {
  return (
    <svg
      className={className(active, styles.icon)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 8V4H8" />
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <line className={styles.eye} style={originAt(9, 14)} x1="9" x2="9" y1="13" y2="15" />
      <line className={styles.eye} style={originAt(15, 14)} x1="15" x2="15" y1="13" y2="15" />
    </svg>
  );
}
